// src/components/Mensalistas.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../api";
import "./Mensalistas.css";

const strip = (s = "") =>
  s.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

const renderNome = (p) =>
  p.display_name || p.apelido || p.nome || p?.jogadores?.apelido || "Sem nome";

const Mensalistas = ({ peladaId: groupId, onStartLoading, onFinishLoading }) => {
  const [mensalistas, setMensalistas] = useState([]);
  const [membros, setMembros] = useState([]);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem("authToken")}` }),
    []
  );

  useEffect(() => {
    const fetchAll = async () => {
      if (!groupId) {
        setError("Group ID não encontrado.");
        return;
      }
      onStartLoading?.();
      try {
        const [mResp, memResp] = await Promise.all([
          api.get(`/api/groups/${groupId}/mensalistas`, { headers }),
          api.get(`/api/groups/${groupId}/members`, { headers }),
        ]);

        const mensal = (mResp.data || []).map((row) => {
          if (row?.player)
            return { ...row.player, player_id: row.player.id || row.player.player_id };
          return row;
        });

        const members = (memResp.data || []).map((row) => {
          if (row?.player)
            return { ...row.player, player_id: row.player.id || row.player.player_id };
          return row;
        });

        const mensalIds = new Set(mensal.map((m) => m.player_id || m.id));
        setMensalistas(mensal);
        setMembros(members.filter((m) => !mensalIds.has(m.player_id || m.id)));
        setError("");
      } catch (err) {
        setError(err?.response?.data?.error || "Erro ao carregar mensalistas.");
      } finally {
        onFinishLoading?.();
      }
    };
    fetchAll();
  }, [groupId, headers, onStartLoading, onFinishLoading]);

  const match = useCallback((p, q) => {
    if (!q) return true;
    const n = renderNome(p);
    const jid = p.wa_jid || p.player_id || p.id || "";
    return strip(n).includes(q) || strip(jid).includes(q);
  }, []);

  const q = strip(query);
  const mensalistasFiltrados = useMemo(
    () => mensalistas.filter((p) => match(p, q)),
    [mensalistas, match, q]
  );
  const membrosFiltrados = useMemo(
    () => membros.filter((p) => match(p, q)),
    [membros, match, q]
  );

  const toggleMensalista = async (playerId, isMensalista) => {
    try {
      await api.post(
        `/api/groups/${groupId}/mensalistas`,
        { playerId, mensalista: !isMensalista },
        { headers: { ...headers, "Content-Type": "application/json" } }
      );

      if (isMensalista) {
        const remov = mensalistas.find((m) => (m.player_id || m.id) === playerId);
        setMensalistas((prev) => prev.filter((m) => (m.player_id || m.id) !== playerId));
        if (remov) setMembros((prev) => [...prev, remov]);
      } else {
        const add = membros.find((m) => (m.player_id || m.id) === playerId);
        setMembros((prev) => prev.filter((m) => (m.player_id || m.id) !== playerId));
        if (add) setMensalistas((prev) => [...prev, add]);
      }
    } catch (err) {
      alert(err?.response?.data?.error || "Erro ao alterar mensalista.");
    }
  };

  if (error) return <p className="error-message">{error}</p>;

  return (
    <div className="mensalistas">
      <div className="mensalistas-head">
        <div className="head-title">
          <h3>Gerenciar Mensalistas</h3>
          <p className="muted">Adicione ou remova mensalistas do grupo</p>
        </div>

        <label className="search">
          <span className="search-icon" aria-hidden>🔎</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nome ou JID/telefone…"
            className="search-input"
            type="text"
            inputMode="search"
          />
          {query && (
            <button
              type="button"
              className="search-clear"
              onClick={() => setQuery("")}
              aria-label="Limpar busca"
              title="Limpar"
            >
              ×
            </button>
          )}
        </label>
      </div>

      <div className="mensalistas-grid">
        {/* MENSALISTAS */}
        <section className="card card-elev">
          <header className="card-head">
            <h4>Mensalistas</h4>
            <span className="badge-count">{mensalistasFiltrados.length}</span>
          </header>

          {mensalistasFiltrados.length > 0 ? (
            <ul className="list">
              {mensalistasFiltrados.map((p) => {
                const id = p.player_id || p.id;
                const nome = renderNome(p);
                return (
                  <li key={`m-${id}`} className="person-card">
                    <div className="person-row-top">
                      <div className="avatar">{nome.charAt(0).toUpperCase()}</div>
                      <div className="person-info">
                        <div className="person-name" title={nome}>{nome}</div>
                        {p.wa_jid && <div className="person-sub">{p.wa_jid}</div>}
                      </div>
                    </div>
                    <div className="person-row-actions">
                      <button
                        className="btn-line btn--danger-line"
                        onClick={() => toggleMensalista(id, true)}
                      >
                        Remover dos mensalistas
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="empty">
              <div className="empty-emoji">📭</div>
              <div className="empty-title">Nada por aqui</div>
              <div className="empty-sub">Nenhum mensalista encontrado.</div>
            </div>
          )}
        </section>

        {/* MEMBROS */}
        <section className="card card-elev">
          <header className="card-head">
            <h4>Jogadores do Grupo</h4>
            <span className="badge-count">{membrosFiltrados.length}</span>
          </header>

          {membrosFiltrados.length > 0 ? (
            <ul className="list">
              {membrosFiltrados.map((p) => {
                const id = p.player_id || p.id;
                const nome = renderNome(p);
                return (
                  <li key={`j-${id}`} className="person-card">
                    <div className="person-row-top">
                      <div className="avatar">{nome.charAt(0).toUpperCase()}</div>
                      <div className="person-info">
                        <div className="person-name" title={nome}>{nome}</div>
                        {p.wa_jid && <div className="person-sub">{p.wa_jid}</div>}
                      </div>
                    </div>
                    <div className="person-row-actions">
                      <button
                        className="btn-line btn--primary-line"
                        onClick={() => toggleMensalista(id, false)}
                      >
                        Tornar mensalista
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="empty">
              <div className="empty-emoji">🔎</div>
              <div className="empty-title">Sem resultados</div>
              <div className="empty-sub">Ajuste a sua busca para encontrar o jogador.</div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Mensalistas;
